// Which venue or organisation the page is about, resolved from the URL's slug against the member's
// own venues and organisations — never from better-auth's `activeOrganizationId`, which is only a
// UI default (spec/07 §6.1). Every request below names the scope in its path (spec/08 §1.3).

import { createContext, useContext, type ReactNode } from 'react';

import type { AccessScope, MyOrganization, MyVenue } from '../api/me';

export type ConsoleScope =
  | { readonly kind: 'venue'; readonly venue: MyVenue; readonly organization: MyOrganization | undefined }
  | { readonly kind: 'organization'; readonly organization: MyOrganization };

const ScopeContext = createContext<ConsoleScope | null>(null);

export function ScopeProvider({ scope, children }: { readonly scope: ConsoleScope; readonly children: ReactNode }) {
  return <ScopeContext.Provider value={scope}>{children}</ScopeContext.Provider>;
}

export function useConsoleScope(): ConsoleScope {
  const scope = useContext(ScopeContext);
  if (scope === null) throw new Error('useConsoleScope() is for pages inside a venue or organisation layout.');
  return scope;
}

export function useCurrentVenue(): MyVenue {
  const scope = useConsoleScope();
  if (scope.kind !== 'venue') throw new Error('useCurrentVenue() is for pages inside a venue.');
  return scope.venue;
}

/**
 * The ids a page shares whether it opened at a venue or at the organisation, for the people screens
 * that read the same organisation-scoped api from either place (spec/13 §8, F-ORG-05, F-IAM-08). The
 * `venueId` is present at a venue and absent at the organisation; `access` is the scope the permission
 * set is read for (`GET /v1/me/access`), which is the venue at a venue and the organisation above it.
 */
export interface ScopeIds {
  readonly organizationId: string;
  readonly venueId: string | undefined;
  readonly venueName: string | undefined;
  /** The venue's currency at a venue; unknown above one. */
  readonly currency: string | undefined;
  readonly access: AccessScope;
}

export function useScopeIds(): ScopeIds {
  const scope = useConsoleScope();
  if (scope.kind === 'venue') {
    return {
      organizationId: scope.venue.organizationId,
      venueId: scope.venue.id,
      venueName: scope.venue.name,
      currency: scope.venue.currency,
      access: { venueId: scope.venue.id },
    };
  }
  return { organizationId: scope.organization.id, venueId: undefined, venueName: undefined, currency: undefined, access: { organizationId: scope.organization.id } };
}
